import React, { useState, useRef, useEffect } from 'react';

export default function ComboBox({ options, value, onChange, placeholder, label, autoGrow, examples, fullWidth }) {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filtered, setFiltered] = useState(options);
  const inputRef = useRef();
  const spanRef = useRef();
  const [inputWidth, setInputWidth] = useState(180);
  const [clearedForOther, setClearedForOther] = useState(false);
  const [lastSelectedKey, setLastSelectedKey] = useState('');

  useEffect(() => {
    // If value is set externally, update inputValue
    const found = options.find(opt => opt.key === value || opt.label === value);
    setInputValue(found ? found.label : value || '');
    setClearedForOther(false);
    setLastSelectedKey(value);
  }, [value, options]);

  useEffect(() => {
    // Always show all options when dropdown is open
    if (showDropdown) {
      setFiltered(options);
    } else {
      setFiltered([]);
    }
  }, [options, showDropdown]);

  useEffect(() => {
    if (autoGrow && spanRef.current) {
      setInputWidth(Math.min(Math.max(spanRef.current.offsetWidth + 48, 160), 420));
    }
  }, [inputValue, autoGrow]);

  function handleSelect(opt) {
    setInputValue(opt.label);
    setLastSelectedKey(opt.key);
    onChange(opt.key);
    // Do not close dropdown here; let blur handle it
  }

  function handleInputChange(e) {
    setInputValue(e.target.value);
    setShowDropdown(true);
    setClearedForOther(true);
    onChange(e.target.value); // Allow free text
  }

  function handleFocus() {
    // If 'Other' is selected and not yet cleared, clear the input for typing
    if ((value === 'other' || inputValue === 'Other') && !clearedForOther) {
      setInputValue('');
      setClearedForOther(true);
      onChange('');
    }
    setShowDropdown(true);
  }

  function handleBlur() {
    setTimeout(() => setShowDropdown(false), 120); // Delay to allow click
  }

  function handleArrowMouseDown(e) {
    e.preventDefault();
    inputRef.current.focus();
    setShowDropdown(true);
  }

  // Determine if 'Other' is selected
  const isOther = lastSelectedKey === 'other';
  const isEmpty = !inputValue;
  const showGray = isEmpty || (isOther && inputValue === 'Other');

  const containerStyle = {
    display: fullWidth ? 'block' : 'inline-block', 
    position: 'relative', 
    minWidth: fullWidth ? '100%' : 160, 
    maxWidth: fullWidth ? '100%' : 420,
    marginBottom: 16
  };

  const inputStyle = {
    fontSize: fullWidth ? 16 : 24,
    fontWeight: 600,
    border: fullWidth ? '2px solid #A389F4' : 'none',
    borderRadius: fullWidth ? 12 : 0,
    borderBottom: fullWidth ? 'none' : '2px solid #A389F4',
    background: 'white',
    color: showGray ? '#B0B0B0' : '#222',
    width: fullWidth ? '100%' : (autoGrow ? inputWidth : 180),
    minWidth: fullWidth ? '100%' : 120,
    margin: fullWidth ? 0 : '0 8px',
    paddingRight: 28,
    paddingLeft: 12,
    transition: 'width 0.2s, color 0.2s',
  };

  const dropdownStyle = {
    position: 'absolute',
    left: 0,
    top: fullWidth ? 42 : 38,
    background: 'white',
    border: '1.5px solid #A389F4',
    borderRadius: 8,
    boxShadow: '0 2px 12px #A389F422',
    zIndex: 10,
    minWidth: fullWidth ? '100%' : 180,
    maxWidth: fullWidth ? '100%' : 420,
    width: fullWidth ? '100%' : 'auto',
    overflowY: 'auto',
  };

  return (
    <div style={containerStyle} className="combo-box">
      {label && <span style={{ fontWeight: 500, marginRight: 6 }}>{label}</span>}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        placeholder={placeholder}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={inputStyle}
        autoComplete="off"
      />
      {/* Hidden span for measuring text width */}
      {autoGrow && (
        <span
          ref={spanRef}
          style={{
            position: 'absolute',
            visibility: 'hidden',
            height: 0,
            overflow: 'hidden',
            whiteSpace: 'pre',
            fontSize: 24,
            fontWeight: 600,
            fontFamily: 'inherit',
            pointerEvents: 'none',
            paddingLeft: 12,
          }}
        >
          {inputValue || placeholder || ''}
        </span>
      )}
      {/* Down arrow icon */}
      <span
        onMouseDown={handleArrowMouseDown}
        style={{
          position: 'absolute',
          right: 18,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 18,
          color: '#A389F4',
          userSelect: 'none',
          cursor: 'pointer',
        }}
      >
        ▼
      </span>
      {showDropdown && filtered.length > 0 && (
        <div style={dropdownStyle}>
          {filtered.map(opt => (
            <div
              key={opt.key}
              onMouseDown={() => handleSelect(opt)}
              style={{
                padding: '8px 14px',
                cursor: 'pointer',
                background: opt.key === 'other' ? '#F8F8FA' : 'white',
                color: opt.key === 'other' ? '#888' : '#222',
                fontWeight: 500,
                fontSize: 18,
              }}
            >
              {opt.label}
            </div>
          ))}
          {/* Example suggestions: always show if 'Other' is selected */}
          {examples && isOther && showDropdown && (
            <div style={{ padding: '8px 14px', color: '#A389F4', fontSize: 15, opacity: 0.7, borderTop: '1px solid #F0F0F0' }}>
              Try: {examples.map((ex, i) => <span key={i} style={{ marginRight: 8, fontStyle: 'italic' }}>{ex}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 